import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "@/i18n/I18nProvider";
import { COMPANY, CANONICAL_ORIGIN } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

interface SEOProps {
  routeKey: RouteKey;
  titleOverride?: string;
  description?: string;
  /** Set true on error pages (404) so they are excluded from the index. */
  noindex?: boolean;
}

/**
 * Minimal client-side SEO manager.
 * Sets <title>, description, robots, canonical, hreflang alternates,
 * Open Graph / Twitter tags and JSON-LD
 * (Organization + WebSite + LocalBusiness + ContactPoint).
 * Canonical domain: https://bnmgroup.it only.
 */
export function SEO({ routeKey, titleOverride, description, noindex = false }: SEOProps) {
  const { t, locale } = useT();
  const { pathname } = useLocation();

  useEffect(() => {
    const routeMeta = t.routes[routeKey];
    const pageTitle = titleOverride ?? routeMeta.title;
    const composed =
      routeKey === "home"
        ? `${t.meta.company} — ${t.meta.tagline}`
        : `${pageTitle} · ${t.meta.company}`;

    document.title = composed;

    const desc =
      description ??
      ("lede" in routeMeta && routeMeta.lede
        ? (routeMeta.lede as string)
        : t.meta.tagline);

    const canonical = `${CANONICAL_ORIGIN}${pathname}`;

    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", canonical);
    setHreflangAlternates(pathname);
    setMeta("name", "description", desc);
    setMeta("property", "og:title", composed);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:site_name", COMPANY.legalName);
    setMeta("property", "og:locale", locale === "it" ? "it_IT" : "en_GB");
    setMeta("property", "og:image", `${CANONICAL_ORIGIN}/og-image.png`);
    setMeta("name", "twitter:image", `${CANONICAL_ORIGIN}/og-image.png`);
    setMeta("name", "twitter:title", composed);
    setMeta("name", "twitter:description", desc);
    setJsonLd();
  }, [routeKey, titleOverride, description, noindex, t, locale, pathname]);

  return null;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Emits <link rel="alternate" hreflang="…"> for the EN/IT variants of the
 * current path, mirroring the alternates already declared in sitemap.xml.
 */
function setHreflangAlternates(pathname: string) {
  const rest = pathname.replace(/^\/(en|it)(?=\/|$)/, "");
  const alternates: Array<[string, string]> = [
    ["en", `${CANONICAL_ORIGIN}/en${rest}`],
    ["it", `${CANONICAL_ORIGIN}/it${rest}`],
    ["x-default", `${CANONICAL_ORIGIN}/en${rest}`],
  ];
  for (const [hreflang, href] of alternates) {
    let el = document.querySelector<HTMLLinkElement>(
      `link[rel="alternate"][hreflang="${hreflang}"]`
    );
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", hreflang);
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  }
}

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: COMPANY.address.street,
  postalCode: "56022",
  addressLocality: "Castelfranco di Sotto",
  addressRegion: "PI",
  addressCountry: "IT",
};

function setJsonLd() {
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${CANONICAL_ORIGIN}/#organization`,
        name: COMPANY.legalName,
        legalName: COMPANY.legalName,
        url: CANONICAL_ORIGIN,
        logo: `${CANONICAL_ORIGIN}/images/brand/bnm-logo-square.png`,
        image: `${CANONICAL_ORIGIN}/og-image.png`,
        email: COMPANY.email,
        telephone: COMPANY.phone,
        vatID: COMPANY.vatIntl,
        taxID: COMPANY.taxCode,
        address: POSTAL_ADDRESS,
        contactPoint: {
          "@type": "ContactPoint",
          telephone: COMPANY.phone,
          email: COMPANY.email,
          contactType: "sales",
          availableLanguage: ["English", "Italian"],
        },
      },
      {
        "@type": "WebSite",
        "@id": `${CANONICAL_ORIGIN}/#website`,
        name: COMPANY.legalName,
        alternateName: "BNM Group",
        url: CANONICAL_ORIGIN,
        publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
        inLanguage: ["en", "it"],
      },
      {
        "@type": "LocalBusiness",
        "@id": `${CANONICAL_ORIGIN}/#localbusiness`,
        name: COMPANY.legalName,
        url: CANONICAL_ORIGIN,
        image: `${CANONICAL_ORIGIN}/og-image.png`,
        telephone: COMPANY.phone,
        email: COMPANY.email,
        address: POSTAL_ADDRESS,
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: COMPANY.businessHours.weekdays.open,
            closes: COMPANY.businessHours.weekdays.close,
          },
        ],
      },
    ],
  };

  let el = document.getElementById("bnm-jsonld") as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "bnm-jsonld";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
